import React from 'react';

import moment from 'moment';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import { Subscription } from 'relay-runtime';

import {
  TaskStatus,
  usePollStatusQuery as usePollStatusQueryType,
} from 'src/clients/Network/gql/build/usePollStatusQuery.graphql';

const usePollStatusQuery = graphql`
  query usePollStatusQuery($id: String!) {
    task(id: $id) {
      data {
        id
        status
      }
      errors
    }
  }
`;

const POLL_INTERVAL = 1000;
export const TERMINAL_STATUSES = new Set<TaskStatus>(['FAILED', 'SUCCEEDED']);

type Init = (taskId?: string) => void;

const usePollStatus = (timeout: number): [TaskStatus, Init, boolean] => {
  const [taskId, setTaskId] = React.useState<string | undefined>(undefined);
  const [status, setStatus] = React.useState<TaskStatus>('SCHEDULED');
  const environment = useRelayEnvironment();
  const subscriptionRef = React.useRef<Subscription>(undefined);
  const timeoutRef = React.useRef<number>(moment().add(timeout, 'seconds').valueOf());
  const isPolling = taskId != null && !TERMINAL_STATUSES.has(status);

  React.useEffect(() => {
    if (taskId != null) {
      timeoutRef.current = moment().add(timeout, 'seconds').valueOf();
    }
  }, [timeout, taskId]);

  const pollTask = React.useCallback(() => {
    if (taskId != null && environment != null) {
      subscriptionRef.current = fetchQuery<usePollStatusQueryType>(environment, usePollStatusQuery, {
        id: taskId,
      }).subscribe({
        next: (data) => {
          if (data.task.data?.status != null) {
            setStatus(data.task.data.status);
          }
        },
      });
    }
  }, [taskId, environment]);

  React.useEffect(() => {
    return () => {
      if (subscriptionRef.current != null) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, []);

  React.useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    const cb = () => {
      if (moment().valueOf() >= timeoutRef.current && status != 'SUCCEEDED') {
        setStatus('FAILED');
      } else {
        pollTask();
      }
    };
    if (!TERMINAL_STATUSES.has(status)) {
      interval = setInterval(cb, POLL_INTERVAL);
    }
    return () => {
      clearInterval(interval);
    };
  }, [status, pollTask]);

  React.useEffect(() => {
    if (taskId == null) setStatus('SCHEDULED');
  }, [taskId]);

  return [status, setTaskId, isPolling];
};

export default usePollStatus;
